import { makeAutoObservable } from 'mobx';
import { Operation } from 'shared/types/flowstep';
import * as Flow from 'shared/utils/flow';
import { FlowstepCategory, toCategory } from 'shared/utils/flowstep';
import { officeStore, userStore } from 'src/stores';
import { BaseEditorState } from '@v2/components/flowsteps/editors/common/states/base-editor-state';

const isPushOperation = (operation: Operation) => {
  return [Operation.INSERT, Operation.UPDATE, Operation.DELETE].includes(operation);
};

export class FlowstepButtonsState {
  constructor(private readonly baseEditorState:  BaseEditorState) {
    makeAutoObservable(this);
  }

  get flowstep() {
    return this.baseEditorState.flowstep;
  }

  get flowstepMeta() {
    return this.baseEditorState.flowstepMeta;
  }

  get category() {
    return this.flowstep ? toCategory({ operation: this.flowstepMeta.operation }) : null;
  }

  get canSaveCopy() {
    return !!this.flowstep && isPushOperation(this.flowstepMeta.operation);
  }

  get readOnly() {
    if (this.flowstepMeta.operation === Operation.PULL) {
      // depends on license
      // frontend/excel365-addin/src/components/Flowsteps/PullData/PullFlowstep.js:76~83
      return false;
    }
    if (isPushOperation(this.flowstepMeta.operation)) {
      return !userStore.license.flowsteps.push.editExisting.can;
    }
  }

  get allowEditExisting() {
    if (this.baseEditorState.flowstepMeta.operation === Operation.PULL) {
      return !this.readOnly;
    }
    if (isPushOperation(this.baseEditorState.flowstepMeta.operation)) {
      return userStore.license.flowsteps.push.editExisting.can;
    }
  }

  get allowSaveAndDelete() {
    if (this.baseEditorState.flowstepMeta.operation === Operation.PULL) {
      return this.readOnly;
    }
    if (isPushOperation(this.baseEditorState.flowstepMeta.operation)) {
      return userStore.license.flowsteps.push.createOrRemove.unlimited.can;
    }
  }

  get hasPersonalPlan() {
    return userStore.license.hasPersonalPlan;
  }

  get flowsMoveTo() {
    if (!this.flowstep) {
      return [];
    }

    const { flows, flowsteps } = userStore.license;
    const { addFlowsteps } = flows.custom;
    const { unlimited } = flowsteps.push.createOrRemove;

    return officeStore.flows.reduce((out: { key: any; id: any; text: any }[], { id, type, name }: any) => {
      if (id === this.flowstep!.flowId) {
        return out;
      }
      if (
        (Flow.Type.Custom === type && addFlowsteps.can) ||
        (this.category === FlowstepCategory.Pull && Flow.Type.Pull === type) ||
        (this.category === FlowstepCategory.Push && Flow.Type.Push === type && unlimited.can)
      ) {
        out.push({ key: id, id, text: name });
      }
      return out;
    }, []);
  }
}
